import { useTranslation } from '@lb/i18n'
import { useCrew, type CrewChild } from '@lb/domain'
import { IconSettings, cn } from '@lb/ui'

import { http } from '../../lib/client'

/**
 * The sidebar's "My Crew" widget, per the approved parent HTML mock.
 *
 * ── Where it sits and why ────────────────────────────────────────────────
 * Pinned under the primary nav on parent surfaces only — practitioners and
 * TSP members see their `SurfacePanel` in the same slot, and an org admin
 * sees seat administration. A crew widget on those roles would be an
 * empty rail with a "My Crew" heading, which reads as broken; better to
 * not render at all.
 *
 * ── One row per child, plus one row for the co-parent ────────────────────
 * Both come from `GET /api/me/crew`. The child's age is derived at render
 * against today rather than stored — the age is wrong within twelve
 * months of being typed, which is the same reason the backend stores
 * `birthYear` rather than an integer age. When the "Your crew" step was
 * skipped, `children` is empty and `coParent` is null: the widget renders
 * an "Add crew" call-to-action pointing at the profile screen rather than
 * an empty heading, so somebody arriving on the app for the first time
 * has a visible next step.
 *
 * ── Avatar colour ────────────────────────────────────────────────────────
 * Deterministic from the child id (`hueFromId`), so the same child always
 * renders in the same tone across sessions and reloads. Keeps the row
 * visually distinct without needing an editorial colour field on the
 * backend for a decoration.
 */
export function CrewPanel({ onNavigate }: { onNavigate: (to: string) => void }) {
  const { t } = useTranslation()
  const crew = useCrew(http)

  if (crew.isLoading) {
    return (
      <div className="rounded-[var(--r)] border border-border bg-surface p-3">
        <p className="mb-2 px-1 text-[11.5px] font-semibold uppercase tracking-[0.06em] text-muted">
          {t('crew.title')}
        </p>
        {[0, 1].map((i) => (
          <div key={i} className="flex items-center gap-2.5 py-1.5">
            <span className="h-8 w-8 rounded-full bg-cream-dark" aria-hidden />
            <span className="h-3 w-24 rounded-full bg-cream-dark" aria-hidden />
          </div>
        ))}
      </div>
    )
  }

  const children = crew.data?.children ?? []
  const coParent = crew.data?.coParent

  return (
    <div className="rounded-[var(--r)] border border-border bg-surface p-3">
      <div className="mb-1 flex items-center justify-between px-1">
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.06em] text-muted">
          {t('crew.title')}
        </p>
        <button
          type="button"
          onClick={() => onNavigate('/profile')}
          aria-label={t('crew.edit')}
          className="h-6 w-6 rounded-full text-muted transition-colors hover:bg-cream-dark hover:text-ink"
        >
          {/*
           * A GEAR, not a pencil — `crew-edit` in the mock is `IconSettings`.
           * The distinction is not cosmetic: this opens the crew screen, where
           * a child is added, removed and shared with a co-parent. A pencil
           * promises inline renaming of the thing it sits beside.
           */}
          <IconSettings size={15} title={null} className="mx-auto" />
        </button>
      </div>

      {children.length === 0 && !coParent ? (
        <button
          type="button"
          onClick={() => onNavigate('/profile')}
          className="mt-1 flex w-full items-center gap-2.5 rounded-[var(--r-sm)] px-1 py-2 text-left text-[13px] text-muted hover:bg-cream-dark hover:text-ink"
        >
          <span
            aria-hidden
            className="grid h-8 w-8 place-items-center rounded-full border border-dashed border-border-deep text-[16px] text-muted"
          >
            +
          </span>
          {t('crew.empty')}
        </button>
      ) : (
        <div className="flex flex-col">
          {children.map((child) => (
            <CrewRow key={child.id} child={child} />
          ))}
          {coParent ? (
            <div className="flex items-center gap-2.5 rounded-[var(--r-sm)] px-1 py-1.5">
              <span
                aria-hidden
                className="grid h-8 w-8 flex-none place-items-center rounded-full border border-dashed border-border-deep text-[16px] text-muted"
              >
                +
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-ink">
                  {coParent}
                </span>
                <span className="block truncate text-[11px] text-muted">{t('crew.coParent')}</span>
              </span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

function CrewRow({ child }: { child: CrewChild }) {
  const { t } = useTranslation()
  const initial = child.name.trim().charAt(0).toUpperCase() || '?'
  const age = child.birthYear != null ? new Date().getFullYear() - child.birthYear : null

  return (
    <div className="flex items-center gap-2.5 rounded-[var(--r-sm)] px-1 py-1.5">
      {child.photoUrl ? (
        <img src={child.photoUrl} alt="" className="h-8 w-8 flex-none rounded-full object-cover" />
      ) : (
        <span
          aria-hidden
          className={cn(
            /*
             * `--ink-on-tint`, not `--ink`. Every tone below is a FIXED tint
             * that stays bright in dark mode while `--ink` flips to near-white
             * — measured at 1.09:1, a pale letter on a pale disc. Same pair the
             * community chips hit; this token is what it is for.
             */
            'grid h-8 w-8 flex-none place-items-center rounded-full text-[13px] font-semibold text-ink-on-tint',
            avatarTone(child.id),
          )}
        >
          {initial}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold text-ink">{child.name}</span>
        {age != null ? (
          <span className="block truncate text-[11px] text-muted">
            {t('crew.age', { count: age })}
          </span>
        ) : null}
      </span>
    </div>
  )
}

/**
 * Deterministic avatar background from the child id. Tones drawn from the
 * palette already declared in the design tokens, so a fresh colour cannot
 * silently arrive from a stringly-typed hex.
 *
 * ── These were five colours that did not exist ───────────────────────────
 * The list read `terra-soft`, `sage-soft`, `sky-soft`, `plum-soft`,
 * `honey-soft`. None of those is mapped in `@theme inline`, and there is no
 * `sky` or `plum` in the brand at all. Tailwind emits nothing for a class it
 * cannot resolve, so every avatar computed to `rgba(0,0,0,0)` — measured, not
 * guessed — and five children out of six got a transparent disc with a letter
 * floating on the sidebar. Only `cream-dark` ever drew.
 *
 * The mock gives each child a flat pastel; under the accent policy the washes
 * are the two neutral tints that stay bright in dark mode (so the navy
 * initial below reads on both).
 */
const AVATAR_TONES = ['bg-bleu-gris-light', 'bg-bleu-gris'] as const

function avatarTone(id: string): (typeof AVATAR_TONES)[number] {
  let hash = 0
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) | 0
  return AVATAR_TONES[Math.abs(hash) % AVATAR_TONES.length]!
}
